import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import {
    compactNavigation,
    defaultNavigation,
    futuristicNavigation,
    horizontalNavigation,
} from 'app/mock-api/common/navigation/data';
import { cloneDeep } from 'lodash-es';
import { InterfaceService } from 'app/shared/services/edduca-imob';
import { switchMap, catchError, of } from 'rxjs';
import { AuthService } from 'app/shared/services/core';
import { PerfilEnum } from 'app/shared/models/core/security/perfil.enum';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    private _compactNavigation: FuseNavigationItem[] = compactNavigation;
    private readonly _defaultNavigation: FuseNavigationItem[] = defaultNavigation;
    private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;

    /**
     * Rotas que devem ser filtradas por perfil
     * Mapeamento: rota -> perfis que PODEM ver
     */
    private readonly menuPermissions: { [route: string]: PerfilEnum[] } = {
        '/dashboard': [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA, PerfilEnum.PARCEIRO],
        '/usuario': [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA],
        '/imobiliaria': [PerfilEnum.ADM_SISTEMA],
        '/comissao': [PerfilEnum.ADM_SISTEMA, PerfilEnum.PARCEIRO],
        '/minha-assinatura': [PerfilEnum.ADM_SISTEMA, PerfilEnum.ADM_IMOBILIARIA],
    };

    constructor(
        private _fuseMockApiService: FuseMockApiService,
        private _interfaceService: InterfaceService,
        private _authService: AuthService
    ) {
        this.registerHandlers();
    }

    /**
     * Filtra os itens de navegação baseado no perfil do usuário
     */
    private filterNavigationByProfile(items: FuseNavigationItem[], perfilId: number): FuseNavigationItem[] {
        return items.filter(item => {
            // Verifica se o item tem link e se há restrição para esse link
            if (item.link) {
                const permission = this.menuPermissions[item.link];
                // Se há permissão definida para essa rota, verifica se o perfil tem acesso
                if (permission) {
                    // ADM_SISTEMA sempre tem acesso
                    if (perfilId === PerfilEnum.ADM_SISTEMA) return true;
                    return permission.includes(perfilId);
                }
            }
            // Se não há restrição, permite
            return true;
        });
    }

    registerHandlers(): void {
        this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
            const usuario = this._authService.getUserInToken();

            if (!usuario || !usuario.userId) {
                console.warn('Usuário não autenticado para navegação.');
                return of([
                    200,
                    {
                        compact: [],
                        default: [],
                        futuristic: [],
                        horizontal: [],
                    },
                ]);
            }

            const perfilId = usuario.perfilId || 0;

            return this._interfaceService.obtemInterfaceUsuario(usuario.userId).pipe(
                switchMap((interfaces) => {
                    // Mapeia os itens do backend para o formato de navegação
                    let navigationItems: FuseNavigationItem[] = interfaces.map((item) => ({
                        id: String(item.id),
                        title: item.titulo,
                        type: item.tipo as FuseNavigationItem['type'],
                        icon: item.icone,
                        link: item.link,
                    }));

                    // Filtra os itens baseado no perfil do usuário
                    navigationItems = this.filterNavigationByProfile(navigationItems, perfilId);

                    this._compactNavigation = navigationItems;

                    return of([
                        200,
                        {
                            compact: cloneDeep(this._compactNavigation),
                            default: cloneDeep(this._compactNavigation),
                            futuristic: cloneDeep(this._compactNavigation),
                            horizontal: cloneDeep(this._compactNavigation),
                        },
                    ]);
                }),
                catchError((error) => {
                    console.error('Erro ao carregar navegação do backend:', error);
                    return of([
                        200,
                        {
                            compact: [],
                            default: [],
                            futuristic: [],
                            horizontal: [],
                        },
                    ]);
                })
            );
        });
    }
}
